import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { motion } from "framer-motion";
import {
  Check, Lightning, Sun, Moon, ArrowRight, Buildings,
  Users, Headset, Crown, Rocket, Star, Lock, SpinnerGap
} from "@phosphor-icons/react";
import axios from "axios";
import { toast } from "sonner";

const TIERS = [
  {
    code: "free_beta",
    label: "Free Beta",
    price: 0,
    annual: 0,
    tagline: "Try everything free — no credit card needed",
    badge: "Current",
    badgeColor: "bg-[#FF4400]",
    icon: Lightning,
    seats: "1 seat",
    extraSeats: null,
    comingSoon: false,
    features: [
      "25 pieces of on-brand content per month",
      "1 brand workspace with full voice settings",
      "Upload 5 reference files the AI draws from",
      "Repurpose any piece to 1 additional format",
      "All builders: posts, newsletters, emails, lead magnets, sequences",
      "Set your brand voice, positioning, and tone",
      "AI included — no API key or setup needed",
    ],
    cta: "Get Started Free",
    ctaStyle: "border border-[var(--border-strong)] text-[var(--text-primary)] hover:bg-[var(--bg-hover)]",
    highlighted: false,
  },
  {
    code: "creator",
    label: "Creator",
    price: 49,
    annual: 39,
    tagline: "For solo consultants & creators",
    badge: null,
    badgeColor: "bg-[var(--bg-raised)] border border-[var(--border-strong)]",
    badgeText: "text-[var(--text-secondary)]",
    icon: Rocket,
    seats: "1 seat",
    extraSeats: null,
    billable: true,
    features: [
      "500 pieces of on-brand content per month",
      "Manage up to 10 client brands from one account",
      "15 reference files per workspace",
      "Repurpose to 3 formats at once",
      "AI voice extraction from your real content",
      "Strategic brief builder with AI analysis",
      "Content calendar with draft/review/approved/published workflow",
      "Rewrite individual sections without losing the rest",
      "Automatic draft saving and recovery",
    ],
    cta: "Upgrade to Creator",
    ctaStyle: "border border-[var(--border-strong)] text-[var(--text-primary)] hover:bg-[var(--bg-hover)]",
    highlighted: false,
  },
  {
    code: "studio",
    label: "Studio",
    price: 149,
    annual: 119,
    tagline: "For practices and small teams managing multiple clients",
    badge: "Most Popular",
    badgeColor: "bg-[#FF4400]",
    badgeText: "text-white",
    icon: Crown,
    seats: "3 seats included",
    extraSeats: "+$39/seat/mo",
    billable: true,
    features: [
      "Unlimited content generation*",
      "Up to 50 client brands",
      "25 reference files per workspace",
      "Repurpose to 10 formats at once",
      "Everything in Creator, plus:",
      "AI-powered monthly content strategy planner",
      "Send content to clients for review and approval",
      "Build and export client-ready PDF reports",
      "Content analytics and usage insights",
    ],
    cta: "Upgrade to Studio",
    ctaStyle: "bg-[#FF4400] text-white hover:bg-[#E63E00]",
    highlighted: true,
  },
  {
    code: "agency",
    label: "Agency",
    price: 299,
    annual: 239,
    tagline: "For agencies running content at scale across clients",
    badge: null,
    badgeColor: "bg-[var(--bg-raised)] border border-[var(--border-strong)]",
    badgeText: "text-[var(--text-secondary)]",
    icon: Buildings,
    seats: "5 seats included",
    extraSeats: "+$25/seat/mo",
    billable: true,
    features: [
      "Unlimited content generation*",
      "Unlimited client brands",
      "50 reference files per workspace",
      "Unlimited repurpose chains",
      "Everything in Studio, plus:",
      "Use your own AI key for full model control",
      "White-label portal with your branding",
      "Team roles, permissions, and audit trail",
      "Priority support",
    ],
    cta: "Upgrade to Agency",
    ctaStyle: "border border-[var(--border-strong)] text-[var(--text-primary)] hover:bg-[var(--bg-hover)]",
    highlighted: false,
  },
];

export default function PricingPage() {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [annual, setAnnual] = useState(false);
  const [stripeEnabled, setStripeEnabled] = useState(false);
  const [checkingOut, setCheckingOut] = useState(null);

  useEffect(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${backendUrl}/api/stripe/config`).then(r => setStripeEnabled(r.data.enabled)).catch(() => {});
  }, []);

  const handleCTA = async (tier) => {
    if (tier.code === "free_beta") {
      navigate(user ? "/generate" : "/login");
      return;
    }
    if (!user) { navigate("/login"); return; }
    if (!stripeEnabled) { toast.info("Payments launching soon!"); return; }
    if (!tier.billable) return;

    setCheckingOut(tier.code);
    try {
      const resp = await api.post("/stripe/create-checkout", {
        tier: tier.code,
        billing_cycle: annual ? "annual" : "monthly",
        origin_url: window.location.origin,
      });
      window.location.href = resp.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
      setCheckingOut(null);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-base)]" data-testid="pricing-page">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-[var(--bg-base)]/80 backdrop-blur-xl border-b border-[var(--border)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="hover:opacity-80 transition-opacity flex items-center gap-2">
            <img src="/web_logo.svg" alt="Quibn" className="h-9 w-auto" />
            <span className="text-[10px] text-[var(--text-muted)] hidden sm:inline">Pronounced "KWIB-in"</span>
          </button>
          <div className="flex items-center gap-3">
            <button onClick={toggleTheme} className="p-2 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-raised)] transition-colors">
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            {user ? (
              <button onClick={() => navigate("/generate")} className="px-4 py-2 rounded-lg bg-[#FF4400] hover:bg-[#E63E00] text-white text-sm font-semibold transition-all">
                Dashboard
              </button>
            ) : (
              <>
                <button onClick={() => navigate("/login")} className="text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">
                  Sign In
                </button>
                <button onClick={() => navigate("/login")} className="px-4 py-2 rounded-lg bg-[#FF4400] hover:bg-[#E63E00] text-white text-sm font-semibold transition-all">
                  Get Started
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="pt-16 sm:pt-20 pb-10 text-center px-4">
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--accent-subtle)] text-[#FF4400] text-xs font-semibold mb-5 border border-[#FF4400]/20">
            <Star size={14} weight="fill" />
            Simple, transparent pricing
          </div>
          <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-bold text-[var(--text-primary)] tracking-tight mb-4">
            Plans that grow with you
          </h1>
          <p className="text-[var(--text-secondary)] max-w-xl mx-auto text-base sm:text-lg mb-8">
            Start free, scale when you're ready. Every plan includes all core builders and AI-powered content generation.
          </p>

          {/* Monthly / Annual Toggle */}
          <div className="inline-flex items-center gap-3 p-1 rounded-xl bg-[var(--bg-card)] border border-[var(--border)]">
            <button
              onClick={() => setAnnual(false)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                !annual ? "bg-[#FF4400] text-white shadow-sm" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              }`}
              data-testid="pricing-monthly-toggle"
            >
              Monthly
            </button>
            <button
              onClick={() => setAnnual(true)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                annual ? "bg-[#FF4400] text-white shadow-sm" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              }`}
              data-testid="pricing-annual-toggle"
            >
              Annual
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                annual ? "bg-white/20 text-white" : "bg-[#FF4400]/10 text-[#FF4400]"
              }`}>
                SAVE 20%
              </span>
            </button>
          </div>
        </motion.div>
      </section>

      {/* Pricing Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          {TIERS.map((tier, i) => {
            const price = annual && tier.price > 0 ? tier.annual : tier.price;
            const grayed = tier.billable && !stripeEnabled;
            const isCheckingOut = checkingOut === tier.code;
            return (
              <motion.div
                key={tier.code}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                className={`relative flex flex-col rounded-2xl border p-6 transition-opacity ${
                  grayed ? "opacity-60" : ""
                } ${
                  tier.highlighted && !grayed
                    ? "border-[#FF4400]/50 bg-[var(--bg-card)] shadow-lg shadow-[#FF4400]/5 ring-1 ring-[#FF4400]/20"
                    : "border-[var(--border)] bg-[var(--bg-card)]"
                }`}
                data-testid={`pricing-tier-${tier.code}`}
              >
                {(tier.badge || (grayed && tier.billable)) && (
                  <div className="absolute -top-3 left-5">
                    <span className={`px-3 py-1 rounded-full ${grayed ? "bg-[var(--bg-raised)] border border-[var(--border-strong)] text-[var(--text-secondary)]" : `${tier.badgeColor} ${tier.badgeText || "text-white"}`} text-xs font-semibold`}>
                      {grayed ? "Coming Soon" : tier.badge}
                    </span>
                  </div>
                )}

                <div className="mb-5 mt-1">
                  <div className="flex items-center gap-2 mb-2">
                    <tier.icon size={20} weight="fill" className={grayed ? "text-[var(--text-muted)]" : "text-[#FF4400]"} />
                    <h3 className={`font-['Cormorant_Garamond'] text-lg font-bold ${grayed ? "text-[var(--text-muted)]" : "text-[var(--text-primary)]"}`}>{tier.label}</h3>
                  </div>
                  <p className="text-xs text-[var(--text-muted)]">{tier.tagline}</p>
                </div>

                <div className="mb-5">
                  {tier.price === 0 ? (
                    <span className="font-['Cormorant_Garamond'] text-4xl font-bold text-[var(--text-primary)]">$0</span>
                  ) : (
                    <div className="flex items-baseline gap-1">
                      <span className={`font-['Cormorant_Garamond'] text-4xl font-bold ${grayed ? "text-[var(--text-muted)]" : "text-[var(--text-primary)]"}`}>
                        ${price}
                      </span>
                      <span className="text-sm text-[var(--text-muted)]">/ mo</span>
                    </div>
                  )}
                  {annual && tier.price > 0 && (
                    <p className="text-xs text-[var(--text-muted)] mt-1">
                      ${(price * 12).toLocaleString()} billed once, upfront, for the year
                    </p>
                  )}
                </div>

                {/* Seats */}
                <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-[var(--bg-base)] border border-[var(--border)]">
                  <Users size={14} className="text-[var(--text-muted)] shrink-0" />
                  <span className="text-xs text-[var(--text-secondary)]">{tier.seats}</span>
                  {tier.extraSeats && (
                    <span className="text-xs text-[var(--text-muted)] ml-auto">{tier.extraSeats}</span>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-2.5 mb-6 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className={`flex items-start gap-2 text-sm ${grayed ? "text-[var(--text-muted)]" : "text-[var(--text-secondary)]"}`}>
                      <Check size={14} weight="bold" className={`${grayed ? "text-[var(--text-muted)]" : "text-[#FF4400]"} mt-0.5 shrink-0`} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                {/* Fair Use Footnote */}
                {(tier.code === "studio" || tier.code === "agency") && (
                  <p className="text-[10px] text-[var(--text-muted)] mb-4 leading-relaxed">
                    *Unlimited subject to fair use (10,000/mo).{" "}
                    <span className="text-[#FF4400] cursor-pointer hover:underline" onClick={() => navigate("/fair-use-policy")}>Learn more</span>
                  </p>
                )}

                {/* CTA */}
                <button
                  onClick={() => !grayed && !isCheckingOut && handleCTA(tier)}
                  disabled={grayed || isCheckingOut}
                  className={`w-full py-3 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                    grayed ? "border border-[var(--border)] text-[var(--text-muted)] cursor-not-allowed" : isCheckingOut ? "bg-[var(--bg-raised)] text-[var(--text-muted)] cursor-wait" : `hover:-translate-y-0.5 hover:shadow-md ${tier.ctaStyle}`
                  }`}
                  data-testid={`pricing-cta-${tier.code}`}
                >
                  {isCheckingOut ? <><SpinnerGap size={16} className="animate-spin" /> Redirecting...</> : grayed ? "Coming Soon" : tier.cta}
                </button>
              </motion.div>
            );
          })}
        </div>

        {/* Enterprise */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="mt-8 rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] p-8 flex flex-col md:flex-row items-center justify-between gap-6 opacity-60"
          data-testid="pricing-tier-enterprise"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[var(--bg-raised)] flex items-center justify-center">
              <Headset size={24} weight="fill" className="text-[var(--text-muted)]" />
            </div>
            <div>
              <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[var(--text-muted)]">Enterprise</h3>
              <p className="text-sm text-[var(--text-muted)]">
                Custom seats, volume, SLA, SSO/SAML, and dedicated onboarding. Contact us for pricing.
              </p>
            </div>
          </div>
          <span
            className="shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-[var(--border)] text-sm font-semibold text-[var(--text-muted)] cursor-not-allowed"
            data-testid="pricing-cta-enterprise"
          >
            Coming Soon
          </span>
        </motion.div>
      </section>

      {/* FAQ */}
      <section className="py-16 border-t border-[var(--border)] bg-[var(--bg-card)]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-bold text-[var(--text-primary)] text-center mb-10">
            Common questions
          </h2>
          <div className="space-y-6">
            <FAQ
              q="What is 'Bring Your Own Key' (BYOK)?"
              a="Agency tier requires you to connect your own OpenRouter API key. This means AI token costs are billed directly by OpenRouter at their rates — we don't mark up. You get full control over models and spend. Creator and Studio tiers use our system-managed key."
            />
            <FAQ
              q="How does annual billing work?"
              a="Annual plans are billed upfront for the full year in a single charge — for example, Creator is $468 billed once instead of $49/mo. You save 20% compared to monthly billing. Seat add-ons are billed separately at the listed rate. You can switch between monthly and annual at any time."
            />
            <FAQ
              q="Can I cancel anytime?"
              a="Yes. Cancel self-serve from your account settings. Your plan stays active through the end of the current billing period — no partial refunds, no games, no hoops."
            />
            <FAQ
              q="What happens if I hit my generation limit?"
              a="You'll see an option to upgrade your plan or buy a one-time credit pack. Credit packs come in 100, 250, 500, or 1,000 credits. Your monthly allowance is used first, then purchased credits kick in. No content is lost."
            />
            <FAQ
              q="How do credit packs work?"
              a="Credit packs are one-time purchases that supplement your monthly allowance. Your plan's monthly generations are always used first. Purchased credits roll over month to month and expire 6 months after purchase. Oldest credits are consumed first. Max 2 packs per month. Full details in our Credit Pack Policy."
            />
            <FAQ
              q="What is fair use for unlimited plans?"
              a="Studio and Agency unlimited tiers are subject to a 10,000 generation/month fair use limit. You'll receive a warning at 8,000 and generation pauses at 10,000. Contact us if you need more — we're happy to accommodate."
            />
            <FAQ
              q="How do additional seats work?"
              a="Studio allows extra seats at $39/seat/mo (3 included). Agency allows extra seats at $25/seat/mo (5 included). Seats are billed as part of your monthly invoice."
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-[var(--border)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between">
          <img src="/web_logo.svg" alt="Quibn" className="h-7 w-auto" />
          <div className="flex items-center gap-4 flex-wrap justify-end">
            <a href="/legal/terms" className="text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)]">Terms</a>
            <a href="/legal/privacy" className="text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)]">Privacy</a>
            <a href="/legal/billing" className="text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)]">Billing & Refunds</a>
            <a href="/legal/credits" className="text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)]">Credit Policy</a>
            <a href="/legal/data-retention" className="text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)]">Data Retention</a>
            <a href="/fair-use-policy" className="text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)]">Fair Use</a>
            <span className="text-xs text-[var(--text-muted)]">&copy; {new Date().getFullYear()}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FAQ({ q, a }) {
  return (
    <div className="border-b border-[var(--border)] pb-5">
      <h3 className="font-['Cormorant_Garamond'] text-base font-semibold text-[var(--text-primary)] mb-2">{q}</h3>
      <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{a}</p>
    </div>
  );
}
